import { useEffect, useState, useCallback } from 'react';
import { Search, Send, X, Check, Users,ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignalR } from '../../../../hooks/useSignalR';
import Toast from '../../../../components/Toast/Toast';
import { PhotoImage } from '../../../../components/common/PhotoImage';
import { getSensitiveData } from '../../../../utils/secureStorage';
import { apiVariables } from '../../../../services/api/apiVariables';
import { set } from 'date-fns';

interface Conversation {
  conversationId: number;
  receiverId: string;
  name: string;
  photoId?: string;
  specialty?: string;
}
type Member = {
  userId: string;
  fullName?: string;
  username?: string;
  photoId?: number | null;
  itSpecialtyName?: string;
};

export default function SendMessage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [receivedMembers, setReceivedMembers] = useState<Member[]>([]);

  const {
    isConnected,
    connectionStatus,
    sendBroadcastMessage,
  } = useSignalR({
    autoConnect: true,
    onReceiveMessage: useCallback((message: any) => {
      if (message?.messageId) {
      }
    }, []),
  });

  useEffect(() => {
    const loadUserId = async () => {
      const uid = (await getSensitiveData('userId')) || '';
      setCurrentUserId(uid);
    };
    loadUserId();
  }, []);

  // Load conversations (to select from)
  useEffect(() => {
    if (!currentUserId) return;

    const loadConversations = async () => {
      try {
        setLoading(true);
        const authToken = await getSensitiveData('authToken');
        if (!authToken) return;

        const response = await fetch(apiVariables.GetMessageList, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const result = await response.json();

        if (result?.success && Array.isArray(result.data)) {
          const mapped: Conversation[] = result.data
            .filter((item: any) =>
              item.otherUserId?.toLowerCase() !== currentUserId.toLowerCase()
            )
            .map((item: any) => ({
              conversationId: item.conversationId,
              receiverId: item.otherUserId,
              name: item.otherUserName || 'Unknown',
              photoId: item.photoId || item.profileImageUrl,
              specialty: item.specialty,
            }));
          setConversations(mapped);
        }
      } catch (error) {
        console.error('Failed to load conversations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [currentUserId]);

  // Parse user IDs from URL query params if provided
  useEffect(() => {
    const membersParam = searchParams.get('userIds');

    if (membersParam) {
      try {
        const members = JSON.parse(decodeURIComponent(membersParam));

        if (Array.isArray(members)) {
          setReceivedMembers(members);
          setSelectedUserIds(members.map((m) => m.userId));

        }
      } catch (error) {
        console.error('Invalid members param:', error);
      }
    }
  }, [searchParams]);

  console.log('Received members from query params:', receivedMembers);

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const removeSelectedUser = (userId: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleSend = async () => {
    const trimmedMessage = messageText.trim();
    const recipients = Array.from(
      new Set(
        selectedUserIds
          .map((id) => id.trim())
          .filter((id) => id && id.toLowerCase() !== currentUserId.toLowerCase())
      )
    );

    if (!trimmedMessage) {
      setToast({ message: 'Please enter a message.', type: 'error' });
      return;
    }

    if (recipients.length === 0) {
      setToast({ message: 'Please select at least one recipient.', type: 'error' });
      return;
    }

    if (!isConnected) {
      setToast({ message: 'Connection lost. Please wait...', type: 'error' });
      return;
    }

    setIsSending(true);

    try {
      await sendBroadcastMessage({
        receiverIds: recipients,
        messageContent: trimmedMessage,
      });

      setToast({ message: 'Message sent successfully!', type: 'success' });
      setMessageText('');
      // Preserve selected members after send (as per requirements)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message.';
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsSending(false);
    }
  };

  const filteredMembers = receivedMembers.filter((member) => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) return true;

    return (
      member.fullName?.toLowerCase().includes(query) ||
      member.username?.toLowerCase().includes(query) ||
      member.itSpecialtyName?.toLowerCase().includes(query)
    );
  });

  const selectedMembers = receivedMembers.filter((member) =>
    selectedUserIds.includes(member.userId)
  );
  function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

return (
  <div className="min-h-[calc(100dvh-80px)] overflow-y-auto bg-[#F5F7FA] px-4 pb-4 sm:h-[calc(100vh-80px)] sm:overflow-hidden sm:px-6">
     <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    <div className="flex h-full min-h-0 w-full flex-col">

      {/* Header card */}
      <div className="mb-2 flex flex-shrink-0 items-center justify-between rounded-2xl border border-gray-200 bg-white px-7 py-4 shadow-sm">
        <div className="flex items-center gap-4">
         

          <div>
            <h1 className="text-[28px] font-bold text-gray-900">
              Send Message
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              Select members and send a message.
            </p>
          </div>
        </div>

      </div>

      {/* Search */}
      <div className="mb-3 flex-shrink-0 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />

          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search members to message..."
            className="h-12 w-full rounded-xl border border-gray-200 bg-white pl-12 pr-4 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/10"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-hidden xl:grid-cols-[1fr_420px]">

        {/* Message */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900">
              Message
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Write a message for the selected members.
            </p>
          </div>

          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type your message..."
            disabled={isSending || !isConnected}
            className="min-h-0 flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-brand-primary focus:bg-white focus:ring-2 focus:ring-brand-primary/10"
          />

          <div className="mt-3 flex flex-shrink-0 items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-gray-300'
              }`}
            />

            <span className="text-xs text-gray-500">
              {isConnected
                ? 'Connected'
                : 'Waiting for connection...'}
            </span>
          </div>

          <button
            onClick={handleSend}
            disabled={
              !messageText.trim() ||
              selectedUserIds.length === 0 ||
              isSending ||
              !isConnected
            }
            className="mt-3 flex h-11 flex-shrink-0 items-center justify-center gap-2 rounded-xl bg-brand-primary text-sm font-semibold text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSending ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />

                {selectedUserIds.length > 0
                  ? `Send to ${selectedUserIds.length} ${
                      selectedUserIds.length === 1
                        ? 'member'
                        : 'members'
                    }`
                  : 'Send Message'}
              </>
            )}
          </button>
        </div>

        {/* Members */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">

          {/* Selected Members Header */}
          <div className="flex-shrink-0 border-b border-gray-100 bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">
                Selected Members
              </span>

              <span className="rounded-full bg-brand-primary/10 px-3 py-1 text-xs font-semibold text-brand-primary">
                {selectedMembers.length}
              </span>
            </div>
          </div>

          {/* ONLY THIS AREA SCROLLS */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {loading ? (
              <div className="py-20 text-center text-sm text-gray-500">
                Loading members...
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center">
                <Users className="mb-3 h-10 w-10 text-gray-300" />

                <p className="font-medium text-gray-600">
                  No members found
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredMembers.map((member) => {
                  const isSelected =
                    selectedUserIds.includes(member.userId);

                  return (
                    <button
                      key={member.userId}
                      type="button"
                      onClick={() =>
                        toggleUserSelection(member.userId)
                      }
                      className={`flex h-[88px] w-full items-center gap-4 px-6 text-left transition ${
                        isSelected
                          ? 'bg-blue-50'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      <PhotoImage
                        photoId={member.photoId}
                        alt={
                          member.fullName ||
                          member.username ||
                          'User'
                        }
                        className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
                        fallback={
                          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary text-sm font-semibold text-white">
                            {getInitials(
                              member.fullName ||
                                member.username ||
                                'User'
                            )}
                          </div>
                        }
                      />

                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-[15px] font-semibold text-gray-900">
                          {member.fullName ||
                            member.username ||
                            'Unknown User'}
                        </h3>

                        {member.itSpecialtyName && (
                          <p className="mt-1 truncate text-sm text-gray-500">
                            {member.itSpecialtyName}
                          </p>
                        )}
                      </div>

                      <div
                        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2 ${
                          isSelected
                            ? 'border-brand-primary bg-brand-primary'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSelected && (
                          <Check className="h-4 w-4 text-white" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {toast && (
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(null)}
      />
    )}
  </div>
);
}
